import { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Drawer,
  Typography,
} from "@material-ui/core";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CloseIcon from "@mui/icons-material/Close";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import MaskAndValid from "utils/maskAndValid";

function formatPedidoDate(dateStr) {
  if (!dateStr) return "—";
  try {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return dateStr;
    return d.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateStr;
  }
}

function formatItemLine(item) {
  const qtd = Number(item.Quantidade);
  const nome = (item.NomeProduto || "").trim() || "Produto";
  const valor = MaskAndValid.maskPrice(item.ValorUnitario ?? 0);
  return `${qtd}x ${nome} - R$ ${valor}`;
}

export default function OrdersModal({
  open,
  onClose,
  pedidos = [],
  loading = false,
  fetchPedidoDetalhe,
  theme,
}) {
  const [expandedId, setExpandedId] = useState(null);
  const [detalheCache, setDetalheCache] = useState({});
  const [loadingDetalheId, setLoadingDetalheId] = useState(null);

  const handlePedidoClick = async (pedido) => {
    const id = pedido.IdVitrineVirtualB2bPedido;
    if (expandedId === id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(id);
    if (detalheCache[id]) return;
    if (!fetchPedidoDetalhe) return;
    setLoadingDetalheId(id);
    try {
      const detalhe = await fetchPedidoDetalhe(id);
      setDetalheCache((prev) => ({ ...prev, [id]: detalhe }));
    } finally {
      setLoadingDetalheId((prev) => (prev === id ? null : prev));
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      className="orders-modal-drawer"
      PaperProps={{
        className: "orders-modal-paper",
      }}
    >
      <Box className="orders-modal-container">
        <Box className="orders-modal-header">
          <Box className="orders-header-left">
            <ReceiptLongIcon
              className="orders-header-icon"
              style={{ color: theme.primary }}
            />
            <Typography
              className="orders-header-title brand-font"
              style={{ color: theme.primary }}
            >
              Meus Pedidos
            </Typography>
          </Box>
          <Button onClick={onClose} className="orders-close-btn">
            <CloseIcon />
          </Button>
        </Box>

        <Box className="orders-modal-content custom-scroll">
          {loading ? (
            <Box className="orders-loading">
              <CircularProgress size={40} style={{ color: theme.primary }} />
              <Typography className="orders-loading-text">
                Carregando pedidos...
              </Typography>
            </Box>
          ) : pedidos.length === 0 ? (
            <Box className="orders-empty-state">
              <ReceiptLongIcon className="orders-empty-icon" />
              <Typography className="orders-empty-text">
                Nenhum pedido realizado ainda.
              </Typography>
            </Box>
          ) : (
            <Box className="orders-list">
              {pedidos.map((pedido) => {
                const id = pedido.IdVitrineVirtualB2bPedido;
                const isExpanded = expandedId === id;
                const detalhe = detalheCache[id];
                const isLoadingDetalhe = loadingDetalheId === id;
                return (
                  <Box key={id} className="orders-item-wrapper">
                    <Box
                      className={`orders-item orders-item-clickable${isExpanded ? " orders-item-expanded" : ""}`}
                      onClick={() => handlePedidoClick(pedido)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handlePedidoClick(pedido);
                        }
                      }}
                    >
                      <Box className="orders-item-header">
                        <Typography className="orders-item-protocol">
                          Pedido: #{id}
                        </Typography>
                        <Box className="orders-item-header-right">
                          <Typography className="orders-item-date">
                            {formatPedidoDate(pedido.DataHora)}
                          </Typography>
                          {isExpanded ? (
                            <ExpandLessIcon className="orders-expand-icon" />
                          ) : (
                            <ExpandMoreIcon className="orders-expand-icon" />
                          )}
                        </Box>
                      </Box>
                      {pedido.NomeVitrine ? (
                        <Typography className="orders-item-vitrine">
                          Vitrine: {pedido.NomeVitrine}
                        </Typography>
                      ) : null}
                      <Box className="orders-item-footer">
                        <Typography className="orders-item-qty">
                          {pedido.QuantidadeItens} {pedido.QuantidadeItens === 1 ? "item" : "itens"}
                        </Typography>
                        <Typography
                          className="orders-item-total brand-font"
                          style={{ color: theme.primary }}
                        >
                          R$ {MaskAndValid.maskPrice(pedido.ValorTotal)}
                        </Typography>
                      </Box>
                    </Box>
                    {isExpanded && (
                      <Box className="orders-item-detalhe">
                        {isLoadingDetalhe ? (
                          <Box className="orders-detalhe-loading">
                            <CircularProgress size={24} style={{ color: theme.primary }} />
                            <Typography className="orders-detalhe-loading-text">
                              Carregando itens...
                            </Typography>
                          </Box>
                        ) : detalhe?.Itens?.length > 0 ? (
                          <ul className="orders-detalhe-list">
                            {detalhe.Itens.map((item, idx) => (
                              <li key={idx} className="orders-detalhe-item">
                                {formatItemLine(item)}
                              </li>
                            ))}
                          </ul>
                        ) : detalhe ? (
                          <Typography className="orders-detalhe-empty">
                            Nenhum item encontrado.
                          </Typography>
                        ) : null}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>
    </Drawer>
  );
}
